import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import { store } from './store';
import { Provider } from 'react-redux';
import { CssBaseline } from '@mui/material';

// Strip credentials out of anything on its way to Sentry. The WebSocket URL
// carries the JWT as a query param (websocketManager.js:109), so without this
// live access tokens end up in breadcrumbs and event URLs at a third party.
const scrubUrl = (value) =>
  typeof value === 'string'
    ? value.replace(/([?&](?:token|access_token|jwt)=)[^&\s]+/gi, '$1[Filtered]')
    : value;

// Initialize Sentry — set REACT_APP_SENTRY_DSN env var to enable
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENV || 'development',
    // Without a release, an error cannot be traced back to the deploy that
    // introduced it. Footer/index.js already reads this same var.
    release: process.env.REACT_APP_VERSION,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],

    // Quota control. Sentry meters errors, transactions and replays as three
    // separate categories at the ORG level, and prod exhausted its allowance
    // and started returning 429 — which silently blinds us to real errors.
    // Replays are the tightest allowance (~50-500/month on lower tiers), so
    // they are cut hardest:
    //   - session sampling off entirely; recording 10% of every agent session
    //     on an all-day dashboard was the main drain and had no triage value.
    //   - on-error sampling 1.0 -> 0.05; at 1.0 every error-producing session
    //     flushed a full replay, so replays scaled 1:1 with the error firehose.
    // Errors themselves stay unsampled — they are the point of Sentry — and are
    // trimmed by filtering known-benign noise instead.
    tracesSampleRate: 0.02,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.05,
    tracePropagationTargets: ['localhost', /^https:\/\/.*\.fleetcamagile\.com/],

    // Payload trim: ~340 console.error sites in src/ feed breadcrumbs (they are
    // NOT events — captureConsoleIntegration is not enabled), so the default
    // 100 breadcrumbs is mostly log noise by the time an error fires.
    maxBreadcrumbs: 50,

    // Deliberately narrow. Anything unactionable-by-construction only; real
    // application errors must stay visible or we are blind again by choice.
    ignoreErrors: [
      // Browser layout quirk, fires on resize, never actionable
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // User navigated away mid-request; not a fault
      'AbortError',
      'Non-Error promise rejection captured with value: undefined',
    ],
    denyUrls: [/^chrome:\/\//i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i],

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data?.url) breadcrumb.data.url = scrubUrl(breadcrumb.data.url);
      breadcrumb.message = scrubUrl(breadcrumb.message);
      return breadcrumb;
    },
    beforeSend(event) {
      if (event.request?.url) event.request.url = scrubUrl(event.request.url);
      return event;
    },
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <Provider store={store}>
    <CssBaseline />
    <App />
  </Provider>
);
